import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet } from '../utils/api';
import { calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import Pagination from './Pagination';

// admin-app has no lucide-react dependency (and none may be added), so these
// small inline SVGs stand in for the icons the original dental-clinic-app
// screen imports from lucide-react. Same paths/viewBox as lucide's icon set.
const IconAlertTriangle = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconPackage = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

// This screen is READ-ONLY on purpose.
//
// The admin-console login token carries {admin_id, email, name, status,
// is_super_admin} and no `role`, while the write routes on /api/shop sit behind
// authorizeAdmin, which requires role === 'admin'. Every POST/PUT/DELETE from
// here would answer 403. Rather than show buttons that always fail — or widen
// the admin token, which would retroactively unlock every other authorizeAdmin
// route in the API — the console browses the catalog across branches and the
// clinic app owns the editing.
const Products = () => {
  const { print_lang, currentLanguage } = useLanguage();

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    apiGet('/api/admin/branches')
      .then((res) => {
        const list = res?.data || res || [];
        setBranches(list);
        if (list.length > 0 && !branchId) setBranchId(String(list[0].branch_id));
      })
      .catch(() => setBranches([]));
    // Runs once; the selector drives every later fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBranch = branches.find((b) => String(b.branch_id) === String(branchId));
  // A product with no rate override inherits the branch rate; a stored 0 does not.
  const branchVatRate = branchRate(selectedBranch);

  const catName = (row) =>
    currentLanguage === 'th'
      ? (row.category_name_th || row.category_name_en || '-')
      : (row.category_name_en || '-');

  const productName = (row) =>
    currentLanguage === 'th' ? (row.name_th || row.name) : row.name;

  const fetchProducts = useCallback(async () => {
    if (!branchId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        branch_id: branchId,
        page: currentPage,
        limit: pageSize,
        status: 'all',
      });
      if (searchTerm) params.set('search', searchTerm);
      if (categoryFilter) params.set('category_id', categoryFilter);
      const res = await apiGet(`/api/shop/products?${params}`);
      setProducts(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalCount(res?.pagination?.totalCount || 0);
    } catch (err) {
      console.error(err.message || 'Failed to load products', err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, currentPage, pageSize, searchTerm, categoryFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (!branchId) return;
    apiGet('/api/shop/categories')
      .then((res) => setCategories(res?.data || []))
      .catch(() => setCategories([]));
    apiGet(`/api/shop/products/low-stock?branch_id=${branchId}`)
      .then((res) => setLowStockCount((res?.data || []).length))
      .catch(() => setLowStockCount(0));
  }, [branchId, products.length]);

  // What the customer actually pays for one unit, under this row's VAT mode.
  const priceInclVat = (row) => {
    const rate = resolveVatRate({
      vatMode: row.vat_mode, itemRate: row.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: 1, unit_price: row.sale_price, vat_mode: row.vat_mode, vat_rate: rate,
    }).total;
  };

  const vatLabel = (mode) => ({
    include: print_lang('vat_include', 'Price includes VAT'),
    exclude: print_lang('vat_exclude', 'Price excludes VAT'),
    none: print_lang('vat_none', 'No VAT'),
  }[mode] || print_lang('vat_none', 'No VAT'));

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 className="page-title">{print_lang('products', 'Products')}</h1>
          {lowStockCount > 0 && (
            <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <IconAlertTriangle size={14} />
              {lowStockCount} {print_lang('low_stock', 'Low Stock')}
            </span>
          )}
        </div>
      </div>

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        {print_lang('catalog.readOnly', 'View only — manage the catalog in the clinic app')}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-3">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('search_products', 'Search products...')}</label>
            <input
              type="text"
              className="form-input"
              placeholder={print_lang('search_products', 'Search products...')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('category', 'Category')}</label>
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            >
              <option value="">{print_lang('all_categories', 'All Categories')}</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('branch.name', 'Branch')}</label>
            <select
              className="form-select"
              value={branchId}
              onChange={(e) => { setBranchId(e.target.value); setCurrentPage(1); }}
            >
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <IconPackage size={40} />
            <h3 className="empty-state-title">{print_lang('no_products', 'No products found')}</h3>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>{print_lang('product_name', 'Product Name')}</th>
                    <th>{print_lang('category', 'Category')}</th>
                    <th>{print_lang('unit', 'Unit')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('sale_price', 'Sale Price')}</th>
                    <th>{print_lang('vat_mode', 'VAT')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('price_incl_vat', 'Price incl. VAT')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('stock_quantity', 'Stock')}</th>
                    <th>{print_lang('status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => {
                    const low = Number(p.stock_quantity) <= Number(p.reorder_level);
                    return (
                      <tr key={p.product_id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{productName(p)}</div>
                          {p.brand && <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{p.brand}</div>}
                        </td>
                        <td>{catName(p)}</td>
                        <td>{p.unit || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{Number(p.sale_price).toFixed(2)}</td>
                        <td><span style={{ fontSize: '0.875rem' }}>{vatLabel(p.vat_mode)}</span></td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{priceInclVat(p).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', color: low ? '#dc2626' : undefined, fontWeight: low ? 600 : undefined }}>
                          {Number(p.stock_quantity).toFixed(0)}
                        </td>
                        <td>
                          <span className={`badge ${p.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                            {print_lang(p.status, p.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Products;
